/**
 * Aggregates Firestore offline/sync signals for the UI layer.
 * Firestore remains the source of truth for persistence and write queueing.
 */

import { onSnapshotsInSync } from "firebase/firestore";
import { deriveSyncPhase } from "../../features/offline/syncState";

/** @typedef {'offline' | 'online' | 'sync_started' | 'sync_completed' | 'error'} SyncEventType */

/**
 * @typedef {Object} SyncSnapshotMeta
 * @property {boolean} fromCache
 * @property {boolean} hasPendingWrites
 */

/**
 * @typedef {Object} SyncServiceState
 * @property {import('../../features/offline/syncState').SyncPhase} phase
 * @property {boolean} browserOnline
 * @property {boolean} snapshotsInSync
 * @property {boolean} hasPendingWrites
 * @property {boolean} servingFromCache
 * @property {boolean} persistenceReady
 * @property {string | null} lastError
 * @property {string | null} activeRoomId
 */

const SNAPSHOT_SOURCES = ["match", "live"];

/** @type {import('firebase/firestore').Firestore | null} */
let dbInstance = null;
/** @type {(() => void) | null} */
let unsubSnapshotsInSync = null;

let browserOnline = typeof navigator !== "undefined" ? navigator.onLine : true;
let snapshotsInSync = true;
let persistenceReady = false;
/** @type {string | null} */
let lastError = null;
/** @type {string | null} */
let activeRoomId = null;

/** @type {Record<string, Partial<Record<string, SyncSnapshotMeta>>>} */
const metaByRoom = {};

/** @type {Set<(state: SyncServiceState) => void>} */
const stateListeners = new Set();

/** @type {Set<(event: { type: SyncEventType, detail?: object }) => void>} */
const eventListeners = new Set();

let lastEmittedPhase = null;
let wasInSync = true;

function roomMeta(roomId) {
  if (!metaByRoom[roomId]) metaByRoom[roomId] = {};
  return metaByRoom[roomId];
}

function aggregateActiveMeta() {
  if (!activeRoomId) {
    return { hasPendingWrites: false, servingFromCache: false };
  }
  const bucket = metaByRoom[activeRoomId] || {};
  let hasPendingWrites = false;
  let servingFromCache = false;
  SNAPSHOT_SOURCES.forEach((source) => {
    const m = bucket[source];
    if (!m) return;
    if (m.hasPendingWrites) hasPendingWrites = true;
    if (m.fromCache) servingFromCache = true;
  });
  return { hasPendingWrites, servingFromCache };
}

function buildState() {
  const { hasPendingWrites, servingFromCache } = aggregateActiveMeta();
  const phase = deriveSyncPhase({
    browserOnline,
    snapshotsInSync,
    hasPendingWrites,
    servingFromCache,
    persistenceReady,
    lastError,
  });
  return {
    phase,
    browserOnline,
    snapshotsInSync,
    hasPendingWrites,
    servingFromCache,
    persistenceReady,
    lastError,
    activeRoomId,
  };
}

function emitEvents(state) {
  if (state.phase !== lastEmittedPhase) {
    if (state.phase === "offline" || state.phase === "waiting_to_sync") {
      emitSyncEvent("offline", { phase: state.phase });
    }
    if (
      lastEmittedPhase === "offline" ||
      lastEmittedPhase === "waiting_to_sync"
    ) {
      if (
        state.phase === "connecting" ||
        state.phase === "saving" ||
        state.phase === "live"
      ) {
        emitSyncEvent("online", { phase: state.phase });
      }
    }
    if (
      state.phase === "saving" ||
      state.phase === "connecting" ||
      state.phase === "waiting_to_sync"
    ) {
      emitSyncEvent("sync_started", { phase: state.phase });
    }
    if (state.phase === "live" && lastEmittedPhase && lastEmittedPhase !== "live") {
      emitSyncEvent("sync_completed", { phase: state.phase });
    }
    if (state.phase === "error") {
      emitSyncEvent("error", { message: state.lastError });
    }
    lastEmittedPhase = state.phase;
  }

  if (!wasInSync && state.snapshotsInSync && state.browserOnline) {
    emitSyncEvent("sync_completed", { source: "onSnapshotsInSync" });
  }
  wasInSync = state.snapshotsInSync;
}

function notify() {
  const state = buildState();
  emitEvents(state);
  stateListeners.forEach((fn) => {
    try {
      fn(state);
    } catch {
      /* listener */
    }
  });
}

/**
 * Extension point for diagnostics, analytics, toasts, background sync.
 * @param {(event: { type: SyncEventType, detail?: object }) => void} handler
 * @returns {() => void}
 */
export function onSyncEvent(handler) {
  eventListeners.add(handler);
  return () => eventListeners.delete(handler);
}

/** @param {SyncEventType} type */
function emitSyncEvent(type, detail) {
  const payload = { type, detail };
  eventListeners.forEach((fn) => {
    try {
      fn(payload);
    } catch {
      /* listener */
    }
  });
}

/**
 * @param {(state: SyncServiceState) => void} listener
 * @returns {() => void}
 */
export function subscribeSyncState(listener) {
  stateListeners.add(listener);
  listener(buildState());
  return () => stateListeners.delete(listener);
}

/**
 * @param {import('firebase/firestore').Firestore} db
 * @param {{ persistenceReady: boolean, error?: string | null }} persistence
 */
export function initFirestoreSyncService(db, persistence) {
  dbInstance = db;
  persistenceReady = Boolean(persistence.persistenceReady);
  lastError = persistence.error || null;

  unsubSnapshotsInSync?.();
  unsubSnapshotsInSync = onSnapshotsInSync(db, () => {
    snapshotsInSync = true;
    notify();
  });

  notify();
}

export function markSnapshotsOutOfSync() {
  snapshotsInSync = false;
  notify();
}

export function setBrowserOnline(online) {
  if (browserOnline === online) return;
  browserOnline = online;
  if (!online) {
    snapshotsInSync = false;
  }
  notify();
}

/** @param {string | null} roomId */
export function setActiveSyncRoom(roomId) {
  if (activeRoomId === roomId) return;
  activeRoomId = roomId;
  notify();
}

/**
 * @param {string} roomId
 * @param {'match' | 'live'} source
 * @param {SyncSnapshotMeta} meta
 */
export function reportSnapshotMetadata(roomId, source, meta) {
  if (!roomId) return;
  roomMeta(roomId)[source] = {
    fromCache: Boolean(meta.fromCache),
    hasPendingWrites: Boolean(meta.hasPendingWrites),
  };
  if (!meta.fromCache && activeRoomId === roomId) {
    snapshotsInSync = true;
  }
  if (meta.fromCache || meta.hasPendingWrites) {
    snapshotsInSync = false;
  }
  notify();
}

/** @returns {SyncServiceState} */
export function getSyncStateSnapshot() {
  return buildState();
}

/** Test-only reset */
export function __resetFirestoreSyncServiceForTests() {
  unsubSnapshotsInSync?.();
  unsubSnapshotsInSync = null;
  dbInstance = null;
  browserOnline = true;
  snapshotsInSync = true;
  persistenceReady = false;
  lastError = null;
  activeRoomId = null;
  Object.keys(metaByRoom).forEach((k) => delete metaByRoom[k]);
  stateListeners.clear();
  eventListeners.clear();
  lastEmittedPhase = null;
  wasInSync = true;
}

export { dbInstance as __dbForTests };
